<template>
  <div class="message-card" ref="card">
    <btn class="message-card__del-btn" icon="times" @click="$emit('remove', val.uuid)"></btn>
    <div
      class="message-card__image-wrapper"
      :class="{'message-card__image-wrapper--square': aspectRatio === 'square'}"
    >
      <div
        class="message-card__image"
        :class="{'message-card__image--square': aspectRatio === 'square'}"
        v-show="image.previewUrl || val.image_url"
        :style="'background: ' + ((image.previewUrl || val.image_url) ? 'url(\'' + (image.previewUrl || val.image_url) + '\')' : 'rgba(0, 0, 0, 0)')"
      >
        <div class="message-card__empty message-card__image-edit" ref="editOverlay">
          <div
            @contextmenu="toggleImagePopover"
            v-show="!showImageSettings"
            class="message-card__image-edit-content"
          >
            <input
              @change="fileInput(true)"
              type="file"
              class="message-card__file-input"
              ref="updatedImage"
              accept="image/jpeg, image/png"
            >
            <div class="message-card__image-edit-icon">
              <fai icon="file-image"></fai>
            </div>
            <div
              class="message-card__image-edit-text"
            >{{ $t('view.flow.blockElements.card.editImage') }}</div>
            <div
              class="message-card__image-edit-text--smaller"
            >{{ $t('view.flow.blockElements.card.rightClick') }}</div>
          </div>
          <div
            @contextmenu="toggleImagePopover"
            v-show="showImageSettings"
            class="message-card__image-edit-settings"
          >
            <div class="message-card__image-edit-wrapper">
              <div class="message-card__image-settings-row">
                <sw-button
                  :swStyle="{width: '16px', height: '16px'}"
                  :contStyle="{height: '16px'}"
                  class="message-card__image-edit-switch"
                  v-model="image.resize"
                  labelColor="white"
                  :label="$t('view.flow.blockElements.card.resize')"
                  @input="onInput"
                ></sw-button>
                <min-sel
                  color="white"
                  class="message-card__image-settings-select"
                  v-model="image.resizeType"
                >
                  <option value="contain">{{ $t('view.flow.blockElements.card.contain') }}</option>
                  <option value="cover">{{ $t('view.flow.blockElements.card.cover') }}</option>
                </min-sel>
              </div>
              <div
                class="message-card__image-settings-row message-card__image-settings-row--fstart"
              >
                <sw-button
                  :swStyle="{width: '16px', height: '16px'}"
                  :contStyle="{height: '16px'}"
                  class="message-card__image-edit-switch"
                  v-model="image.optimize"
                  labelColor="white"
                  :label="$t('view.flow.blockElements.card.optimize')"
                  @input="onInput"
                ></sw-button>
              </div>

              <btn
                small
                icon="magic"
                class="message-card__image-settings-button"
                @click="runImageOperations"
                :inactive="!image.originalUrl"
              >{{ $t('btn.apply') }}</btn>
            </div>
          </div>
        </div>
      </div>
      <div v-show="!val.image_url && !image.previewUrl" class="message-card__empty">
        <input
          @change="fileInput(false)"
          type="file"
          class="message-card__file-input"
          ref="newImage"
          accept="image/jpeg, image/png"
        >
        <div class="message-card__empty-content">
          <div class="message-card__empty-icon">
            <fai icon="file-image"></fai>
          </div>
          <div class="message-card__empty-text">{{ $t('view.flow.blockElements.card.addImage') }}</div>
        </div>
      </div>
    </div>
    <div class="message-card__text">
      <textarea-autosize
        rows="1"
        class="message-card__title message-card__textarea"
        v-model="val.title[$store.state.flowEditorLocale]"
        maxlength="80"
        @keyup.native="onInput"
      ></textarea-autosize>
      <textarea-autosize
        rows="1"
        class="message-card__subtitle message-card__textarea"
        v-model="val.subtitle[$store.state.flowEditorLocale]"
        maxlength="80"
        @keyup.native="onInput"
      ></textarea-autosize>
      <input
        type="text"
        class="message-card__default-url-input"
        v-model="val.default_url"
        :placeholder="$t('view.flow.blockElements.card.defaultUrl')"
      >
    </div>
    <div class="message-card__buttons">
      <message-button
        v-for="(button, n) in val.buttons"
        :key="button.uuid"
        :data="button"
        :n="n"
        :cardParent="$refs.card"
        :lastZIndex="lastZIndex"
        @change="buttonChange($event, button.uuid, val.uuid)"
        @delete="buttonChange($event, button.uuid, val.uuid, true)"
        @popoverOpen="$emit('popoverOpen')"
      ></message-button>
      <div
        class="message-card__button-add-button"
        @click="addButton()"
        v-if="val.buttons.length < 3"
      >
        <fai icon="plus"></fai>
      </div>
    </div>
  </div>
</template>

<script>
import jimp from 'jimp/es'

export default {
  data() {
    return {
      val: null,
      showImageSettings: false,
      image: {
        file: null,
        previewUrl: null,
        originalFile: null,
        originalUrl: null,
        resize: false,
        optimize: true,
        resizeType: 'contain'
      },
      default: {
        button: {
          text: {},
          action: 'redirect',
          data: {
            target: null
          }
        }
      }
    }
  },
  props: ['value', 'aspectRatio', 'lastZIndex'],
  created() {
    this.val = this.value
    for (let locale in this.$store.state.flowLocales) {
      this.default.button.text[locale] = 'New button'
    }
  },
  beforeDestroy() {
    if (this.image.originalUrl) URL.revokeObjectURL(this.image.originalUrl)
  },
  methods: {
    onInput() {
      this.$emit('input', this.val)
    },

    buttonChange(changed, uuid, stepUuid, del) {
      for (let i in this.val.buttons) {
        if (this.val.buttons[i].uuid !== uuid) continue
        if (!del) this.$set(this.val.buttons, i, changed)
        else this.$delete(this.val.buttons, i)
        this.onInput()
        break
      }
    },

    addButton() {
      const i = this.val.buttons.length
      let el = JSON.parse(JSON.stringify(this.default.button))
      el.uuid = this.uid()
      this.$set(this.val.buttons, i, el)
      this.onInput()
    },

    fileInput(edit) {
      if (edit) {
        this.image.file = this.$refs.updatedImage.files[0]
        this.image.originalFile = this.$refs.updatedImage.files[0]
        this.image.originalUrl = URL.createObjectURL(this.image.originalFile)
        this.runImageOperations()
      } else {
        this.image.file = this.$refs.newImage.files[0]
        this.image.originalFile = this.$refs.newImage.files[0]
        this.image.originalUrl = URL.createObjectURL(this.image.originalFile)
        this.runImageOperations()
      }
    },

    toggleImagePopover(e) {
      e.stopPropagation()
      e.preventDefault()
      this.showImageSettings = !this.showImageSettings
    },

    async runImageOperations() {
      try {
        this.$emit('loading')
        if (this.image.previewUrl) URL.revokeObjectURL(this.image.previewUrl)
        const loaded = await jimp.read(this.image.originalUrl)
        const sizes = {
          square: [1000, 1000],
          horizontal: [1280, 670]
        }
        loaded.background(0xFFFFFFFF)
        if (this.image.optimize) loaded.quality(85)
        if (this.image.resize) {
          if (this.image.resizeType === 'contain') loaded.contain(...sizes[this.aspectRatio], jimp.HORIZONTAL_ALIGN_CENTER | jimp.VERTICAL_ALIGN_MIDDLE)
          else loaded.cover(...sizes[this.aspectRatio], jimp.HORIZONTAL_ALIGN_CENTER | jimp.VERTICAL_ALIGN_MIDDLE)
        } else {
          const factorX = loaded.bitmap.width / sizes[this.aspectRatio][0]
          const factorY = loaded.bitmap.height / sizes[this.aspectRatio][1]
          const factor = Math.min(factorX, factorY)
          if (factor > 1) loaded.scale(1 / factor)
        }
        const buffer = await loaded.getBufferAsync(loaded._originalMime)
        const blob = new Blob([buffer], { type: loaded._originalMime })
        const url = URL.createObjectURL(blob)
        this.image.previewUrl = URL.createObjectURL(blob)
        this.image.file = blob
        this.onInput()
        this.$emit('loadingEnd')
      } catch (e) {
        this.$emit('loadingEnd')
        console.error(e)
        this.notify('error', 'view.flow.error.imageOperations')
      }
    },

    setImageUrl(url) {
      this.val.image_url = url
      this.onInput()
    }
  }
}
</script>

<style lang="scss" scoped>
.message-card {
  @include flowElementShadow();
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 7px;
  background-color: white;
  width: 270px;

  &__del-btn {
    position: absolute;
    z-index: 10;
    top: -10px;
    left: -10px;
  }

  &__image-wrapper {
    overflow: hidden;
    border-radius: 7px 7px 0 0;
    position: relative;
    width: 270px;
    padding-top: 52.35%;

    &--square {
      padding-top: 100%;
    }
  }

  &__image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    padding-top: 52.35%;
    background-size: cover !important;
    background-position: 50% 50% !important;

    &--square {
      width: 100%;
      padding-top: 100%;
    }
  }

  &__image-edit {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    background-color: rgba(0, 0, 0, 0.3);
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 1;
    }

    &--always-visible {
      opacity: 1;
    }
  }

  &__image-edit-text {
    &--smaller {
      font-size: 0.8em;
      font-style: italic;
    }
  }

  &__empty {
    @include alignedFlex();
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.4);
    color: white;
    text-align: center;
    transition: background-color 0.2s ease opacity 0.2s ease;

    &:hover {
      background-color: rgba(0, 0, 0, 0.5);
      .message-card__empty-content {
        transform: scale(1.05);
      }
    }
  }

  &__empty-content {
    -webkit-font-smoothing: antialiased;
    will-change: transform;
    transition: transform 0.2s ease;
    cursor: pointer;
  }

  &__file-input {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    opacity: 0;
    cursor: pointer;
    z-index: 8;
  }

  &__text {
    padding: 5px 10px 0 10px;
    flex-grow: 1;
  }

  &__title {
    font-size: 0.95em;
    font-weight: 700;
    font-family: "Lato";
  }

  &__subtitle {
    font-size: 0.9em;
    color: $inactiveGrey;
    margin-bottom: 4px;
    font-family: "Lato";
  }

  &__textarea {
    outline: 0;
    border: 0;
    width: calc(100% - 20px);
  }

  &__button-add-button {
    @include alignedFlex();
    height: 30px;
    border-top: 1px solid $borderGrey;
    transition: background-color 0.2s ease;
    border-radius: 0 0 7px 7px;
    background-color: white;
    color: $danger;
    font-size: 0.9em;
    cursor: pointer;

    &:hover {
      background-color: $messageHover;
    }
  }

  &__image-edit-settings {
    @include alignedFlex();
    position: absolute;
    width: 100%;
    height: 100%;
  }

  &__image-edit-wrapper {
    width: 100%;
  }

  &__image-settings-row {
    @include alignedFlex(space-between);
    width: 55%;
    margin: 0 auto 6px auto;

    &--fstart {
      justify-content: flex-start;
    }
  }

  &__default-url-input {
    border: none;
    border-bottom: 1px solid $borderGrey;
    width: calc(100% - 6px);
    margin: 7px auto 15px auto;
    outline: none !important;
    color: $inactiveGrey;
    padding: 3px;
  }
}
</style>
